import React, { useState, useEffect, useRef } from 'react';
import { Send, Search, User, Paperclip, X, Upload, FolderOpen, FileText, Download, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import PDFViewer from '../ui/PDFViewer';

const Messages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sending, setSending] = useState(false);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [attachmentDetails, setAttachmentDetails] = useState({});
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    try {
      const [messagesRes, clientsRes, filesRes] = await Promise.all([
        api.get('/messages'),
        api.get('/clients'),
        api.get('/files')
      ]);
      setMessages(messagesRes.data);
      setClients(clientsRes.data);
      setFiles(filesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await api.get('/messages');
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleFileSelect = (file) => {
    if (selectedAttachments.find(a => a.id === file.id)) {
      toast.info('File already attached');
      return;
    }
    setSelectedAttachments([...selectedAttachments, file]);
  };

  const handleRemoveAttachment = (fileId) => {
    setSelectedAttachments(selectedAttachments.filter(a => a.id !== fileId));
  };

  const handleNewFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (selectedAttachments.length + files.length > 5) {
      toast.error('Maximum 5 attachments allowed per message');
      return;
    }

    setUploadingFile(true);

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File "${file.name}" exceeds 10MB limit`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/messages/attachments', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setSelectedAttachments(prev => [...prev, {
          id: response.data.id,
          filename: response.data.filename,
          file_size: response.data.file_size,
          mime_type: response.data.mime_type,
          isMessageAttachment: true
        }]);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        toast.error(`Failed to upload "${file.name}"`);
      }
    }

    setUploadingFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedClient || (!messageContent.trim() && selectedAttachments.length === 0)) {
      toast.error('Please select a client and enter a message or attach a file');
      return;
    }

    setSending(true);
    try {
      await api.post('/messages', {
        recipient_id: selectedClient,
        content: messageContent,
        attachments: selectedAttachments.map(a => a.id)
      });
      toast.success('Message sent successfully! Email notification sent to recipient.');
      setMessageContent('');
      setSelectedAttachments([]);
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename, isMessageAttachment = true) => {
    try {
      const endpoint = isMessageAttachment 
        ? `/messages/attachments/${attachmentId}`
        : `/files/${attachmentId}/download`;
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error('Failed to download file');
    }
  };

  const handlePreviewAttachment = async (attachmentId, filename, mimeType) => {
    setPreviewAttachment({ id: attachmentId, filename, mime_type: mimeType });
    
    if (mimeType === 'application/pdf') {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setPreviewUrl(`${backendUrl}/api/messages/attachments/${attachmentId}/preview?token=${token}`);
    } else if (mimeType?.startsWith('image/')) {
      try {
        const response = await api.get(`/messages/attachments/${attachmentId}/preview`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: mimeType });
        setPreviewUrl(URL.createObjectURL(blob));
      } catch (error) {
        console.error('Failed to load preview:', error);
        toast.error('Failed to load preview');
      }
    }
  };

  const closePreview = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewAttachment(null);
    setPreviewUrl(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const renderMessageAttachments = (messageAttachments) => {
    if (!messageAttachments || messageAttachments.length === 0) return null;

    return (
      <div className="mt-2 space-y-1">
        {messageAttachments.map((attachmentId, idx) => (
          <div 
            key={idx}
            className="flex items-center space-x-2 bg-white/20 rounded px-2 py-1 text-xs"
          >
            <FileText className="w-3 h-3" />
            <span className="truncate flex-1">Attachment {idx + 1}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadAttachment(attachmentId, `attachment_${idx + 1}`, true);
              }}
              className="hover:opacity-70"
              title="Download"
            >
              <Download className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  const filteredMessages = selectedClient
    ? messages.filter(m => m.recipient_id === selectedClient || m.sender_id === selectedClient)
    : messages;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="messages-title">Secure Messaging</h2>
        <p className="text-gray-600">Communicate with your clients securely</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client List */}
        <div className="glass rounded-xl p-6">
          <h3 className="font-bold text-gray-900 mb-4">Clients</h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {clients.map((client) => (
              <button
                key={client.id}
                data-testid={`client-${client.id}`}
                onClick={() => setSelectedClient(client.user_id)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedClient === client.user_id
                    ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedClient === client.user_id ? 'bg-white/20' : 'bg-gray-200'
                  }`}>
                    <User className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{client.company_name || 'Unnamed'}</p>
                    <p className={`text-xs truncate ${
                      selectedClient === client.user_id ? 'text-white/80' : 'text-gray-500'
                    }`}>
                      {client.business_type || 'No type'}
                    </p>
                  </div>
                </div>
              </button>
            ))}
            {clients.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No clients yet</p>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="lg:col-span-2 glass rounded-xl flex flex-col" style={{ height: '600px' }}>
          {selectedClient ? (
            <>
              {/* Messages Area */}
              <div className="flex-1 p-6 overflow-y-auto" data-testid="messages-area">
                <div className="space-y-4">
                  {filteredMessages.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No messages yet. Start a conversation!</p>
                  ) : (
                    filteredMessages.map((message, index) => (
                      <div
                        key={message.id}
                        data-testid={`message-${index}`}
                        className={`flex ${message.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`message-bubble ${
                            message.sender_id === user.id ? 'sent' : 'received'
                          }`}
                        >
                          <p>{message.content}</p>
                          {renderMessageAttachments(message.attachments)}
                          <p className={`text-xs mt-1 ${
                            message.sender_id === user.id ? 'text-white/70' : 'text-gray-500'
                          }`}>
                            {new Date(message.sent_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Attachments Preview */}
              {selectedAttachments.length > 0 && (
                <div className="px-6 py-2 border-t border-gray-200 bg-gray-50">
                  <div className="flex flex-wrap gap-2">
                    {selectedAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 border shadow-sm"
                      >
                        {getFileIcon(attachment.mime_type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate max-w-[150px]">{attachment.filename}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(attachment.file_size)}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveAttachment(attachment.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Send Message Form */}
              <form onSubmit={handleSendMessage} className="p-6 border-t border-gray-200">
                <div className="flex space-x-2">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowFileSelector(true)}
                      size="sm"
                      title="Attach from files"
                    >
                      <FolderOpen className="w-4 h-4" />
                    </Button>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleNewFileUpload}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={uploadingFile}
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      title="Upload new file"
                    >
                      {uploadingFile ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  <Textarea
                    data-testid="message-input"
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 resize-none"
                    rows={2}
                  />
                  <Button
                    data-testid="send-message-button"
                    type="submit"
                    disabled={sending}
                    className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Attach up to 5 files (max 10MB each). Supported: Images, PDF, Word, Excel, Text
                </p>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <p>Select a client to start messaging</p>
            </div>
          )}
        </div>
      </div>

      {/* File Selector Dialog */}
      <Dialog open={showFileSelector} onOpenChange={setShowFileSelector}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Files to Attach</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {files.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No files available</p>
            ) : (
              files.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors cursor-pointer hover:bg-gray-50 ${
                    selectedAttachments.find(a => a.id === file.id)
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-gray-200'
                  }`}
                  onClick={() => handleFileSelect(file)}
                >
                  <div className="flex items-center space-x-3">
                    {getFileIcon(file.mime_type)}
                    <div>
                      <p className="font-medium text-gray-900">{file.filename}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.file_size)}
                      </p>
                    </div>
                  </div>
                  {selectedAttachments.find(a => a.id === file.id) && (
                    <div className="text-teal-600 font-medium text-sm">Selected</div>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={() => setShowFileSelector(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attachment Preview Dialog */}
      <Dialog open={!!previewAttachment} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate pr-4">{previewAttachment?.filename}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDownloadAttachment(previewAttachment?.id, previewAttachment?.filename)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(90vh-100px)]">
            {previewAttachment?.mime_type?.startsWith('image/') && previewUrl ? (
              <img 
                src={previewUrl}
                alt={previewAttachment.filename}
                className="w-full h-auto"
              />
            ) : previewAttachment?.mime_type === 'application/pdf' && previewUrl ? (
              <div className="w-full h-[650px]">
                <PDFViewer 
                  url={previewUrl}
                  filename={previewAttachment.filename}
                  onDownload={() => handleDownloadAttachment(previewAttachment.id, previewAttachment.filename)}
                />
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                <Button onClick={() => handleDownloadAttachment(previewAttachment?.id, previewAttachment?.filename)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
